import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Space, Form, Select, Button } from 'antd';
import { useRequest } from 'ahooks';
import {
  getConversationStatistics,
  getCtaStatistics,
  getUsersStatistics,
  getConversationAccuracy,
  getDailyStats,
  getCumulativeStats,
  getBrandRanking,
  getUserActivityRanking,
  getUserActivityRate,
  getUserSubmissionContent,

} from '../services/dashboardService.js';
import { useGlobalState } from '../hooks/useGlobalState.js'
import { getCompanyList } from "../services/userService.js";
import DateRangeSelector from './dashboard/DateRangeSelector.jsx';
// import { useNavigate } from 'react-router-dom';
import SummaryCards from './dashboard/SummaryCards.jsx';
import { buildTimeParams, buildStatsParams, buildDateRangeParams, initialTimeSelection } from './dashboard/timeSelection.js';
import Trends from './dashboard/Trends.jsx';
import RankingPanel from './dashboard/RankingPanel.jsx';
import AdditionalInsights from './dashboard/AdditionalInsights.jsx';

const Dashboard = () => {
  const [form] = Form.useForm();
  // const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  // filters state could be lifted if needed; here we keep local and pass to requests
  const [dateRange, setDateRange] = React.useState(null);
  // For statistics cards (enum-based presets)
  const [timeSelection, setTimeSelection] = React.useState(initialTimeSelection);
  const { userProfile } = useGlobalState();

  // statistics cards
  const {
    data: convStatRes,
    loading: lConv,
    run: runConvStat,
  } = useRequest(
    (params) => getConversationStatistics(params),
    { manual: true }
  );

  const {
    data: ctaStatRes,
    loading: lCta,
    run: runCtaStat,
  } = useRequest(
    (params) => getCtaStatistics(params),
    { manual: true }
  );

  const {
    data: userStatRes,
    loading: lUser,
    run: runUserStat,
  } = useRequest(
    (params) => getUsersStatistics(params),
    { manual: true }
  );

  const {
    data: accuracyRes,
    loading: lAccuracy,
    run: runAccuracy,
  } = useRequest(
    (params) => getConversationAccuracy(params),
    { manual: true }
  );

  // daily vs cumulative
  const [trendMode, setTrendMode] = React.useState('daily');
  const {
    data: dailyRes,
    loading: lDaily,
    run: runDailyStats,
  } = useRequest(
    (params) => getDailyStats(params),
    { manual: true }
  );

  const {
    data: cumRes,
    loading: lCum,
    run: runCumStats,
  } = useRequest(
    (params) => getCumulativeStats(params),
    { manual: true }
  );
  // rankings
  const {
    data: brandRankRes,
    loading: lBrand,
    run: runBrandRank,
  } = useRequest(
    (params) => getBrandRanking(params),
    { manual: true }
  );

  const {
    data: userRankRes,
    loading: lUserRank,
    run: runUserRank,
  } = useRequest(
    (params) => getUserActivityRanking(params),
    { manual: true }
  );
  // additional insights data (self-contained charts will fetch internally)
  const [appliedInsightsParams, setAppliedInsightsParams] = React.useState(null);
  const {
    data: rateRes,
    loading: lRate,
    run: runActivityRate,
  } = useRequest(
    (params) => getUserActivityRate(params),
    { manual: true }
  );

  const {
    data: subRes,
    loading: lSub,
    run: runSubmission,
  } = useRequest(
    (params) => getUserSubmissionContent(params),
    { manual: true }
  );

  const handleSearch = (values) => {
    const companyId =
      values?.company ?? userProfile?.user?.companyId ?? null;

    runAllRequests(companyId, timeSelection);
  };

  const runAllRequests = (companyId, timeSel) => {
    const statParams = {
      ...buildTimeParams(timeSel),
      companyId,
    };

    const statsParams = {
      ...buildStatsParams(timeSel),
      companyId,
    };

    const rankingParams = {
      ...buildTimeParams(timeSel),
      companyId,
    };

    const insightsParams = {
      ...buildDateRangeParams(timeSel),
      companyId,
    };
    setAppliedInsightsParams(insightsParams);

    runConvStat(statParams);
    runCtaStat(statParams);
    runUserStat(statParams);
    runAccuracy(statParams);
    runDailyStats(statsParams);
    runCumStats(statsParams);
    runBrandRank(rankingParams);
    runUserRank(rankingParams);
    runActivityRate(insightsParams);
    runSubmission(insightsParams);
  };

  const resetData = () => {
    form.resetFields();
    setDateRange(null);
    setTimeSelection(initialTimeSelection);
    const companyId = userProfile?.user?.companyId ?? null;

    runAllRequests(companyId, initialTimeSelection);
  };
  useEffect(() => {
    const fetchCompanies = async () => {
      const res = await getCompanyList();
      setCompanies(res?.rows || []);
    };
    fetchCompanies();
    const companyId = userProfile?.user?.companyId ?? null;
    runAllRequests(companyId, initialTimeSelection);
  }, []);

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Card>
        <Space wrap>
          <DateRangeSelector
            value={dateRange}
            onChange={setDateRange}
            onPresetChange={(type, dates) => {
              if (type === 'CUSTOM') {
                setTimeSelection({ timeRangeType: 'CUSTOM', startDate: dates?.[0], endDate: dates?.[1] });
              } else if (type) {
                setTimeSelection({ timeRangeType: type });
              }
            }}
          />

          <Form  style={{ margin: '0 10px' }} form={form} onFinish={handleSearch}>
            <Row gutter={[16, 16]} align="middle">
              {userProfile?.user?.userStatus !== "Company User" && (
                <Form.Item style={{ margin: '0 10px' }} name="company" label="Company">
                  <Select
                    allowClear
                    style={{ width: 180 }}
                    options={companies ? companies.map((company) => ({
                      label: company.companyName,
                      value: company.id,
                    })) : []}
                  ></Select>
                </Form.Item>
              )}
              <Button type="primary" htmlType="submit">
                Search
              </Button>
              <Button onClick={resetData} style={{ marginLeft: '10px' }}>Reset</Button>
            </Row>
          </Form>

        </Space>
      </Card>

      {/* Combined layout: left (Summary + Trends) and right (Ranking) */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <SummaryCards
              data={{
                conversations: convStatRes?.data,
                ctas: ctaStatRes?.data,
                users: userStatRes?.data,
                accuracy: accuracyRes?.data,
              }}
            />
            <Trends
              title={trendMode === 'daily' ? 'Daily Stats' : 'Cumulative Stats'}
              loading={trendMode === 'daily' ? lDaily : lCum}
              data={(trendMode === 'daily' ? dailyRes?.data : cumRes?.data) || {}}
              mode={trendMode}
              onModeChange={setTrendMode}
            />
          </Space>
        </Col>
        <Col xs={24} lg={8}>
          <RankingPanel
            loadingBrand={lBrand}
            loadingUser={lUserRank}
            brandData={brandRankRes?.data}
            userData={userRankRes?.data}
          />
        </Col>
      </Row>

      <AdditionalInsights dateParams={appliedInsightsParams} userActivityRate={rateRes?.data} userSubmissionContent={subRes?.data} />
    </Space>
  );
};

export default Dashboard;


